import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  AI_CHAT_SETTINGS_MODELS,
  AI_CHAT_SETTINGS_PERSONAS,
  AI_CHAT_SETTINGS_VERBOSITY,
  AI_CHAT_SETTINGS_REASONING_EFFORT,
  AI_CHAT_SETTINGS_WEB_SEARCH,
  AI_CHAT_SETTINGS_REASONING_SUMMARY,
} from '@repo/ai-contract';
import type {
  AiChatSettings,
  AiChatSettingsModel,
  AiChatSettingsPersona,
  AiChatSettingsVerbosity,
  AiChatSettingsReasoningEffort,
  AiChatSettingsWebSearch,
  AiChatSettingsReasoningSummary,
} from '@repo/ai-contract';

interface ChatSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  settings: AiChatSettings;
  onSave: (settings: AiChatSettings) => void;
  onReset: () => void;
}

const MODEL_LABELS: Record<AiChatSettingsModel, string> = {
  default: 'Default',
  'gpt-5.4-nano': 'GPT-5.4 Nano',
  'gpt-5.4-mini': 'GPT-5.4 Mini',
  'gpt-5.4': 'GPT-5.4',
  'gpt-5.5': 'GPT-5.5',
  'gpt-5.6-luna': 'GPT-5.6 Luna',
  'gpt-5.6-terra': 'GPT-5.6 Terra',
  'gpt-5.6-sol': 'GPT-5.6 Sol',
  'gpt-5.3-codex': 'GPT-5.3 Codex',
  'gemini-3.1-pro': 'Gemini 3.1 Pro',
  'gemini-3-flash': 'Gemini 3 Flash',
  'claude-sonnet-5': 'Claude Sonnet 5',
  'claude-opus-4-8': 'Claude Opus 4.8',
};

const MODEL_OPTIONS: { value: AiChatSettingsModel; label: string }[] = AI_CHAT_SETTINGS_MODELS.map(
  (value) => ({ value, label: MODEL_LABELS[value] })
);

// Label maps are exhaustive `Record`s over the shared vocabulary, so adding an
// option to @repo/ai-contract forces a label here (a compile error otherwise) and
// the picker derives from the same array — the frontend can't silently omit it.
const PERSONA_LABELS: Record<AiChatSettingsPersona, string> = {
  default: 'Default',
  advisor: 'Advisor',
  guide: 'Guide',
  collaborator: 'Collaborator',
  eager_assistant: 'Eager Assistant',
};

const PERSONA_OPTIONS: { value: AiChatSettingsPersona; label: string }[] =
  AI_CHAT_SETTINGS_PERSONAS.map((value) => ({ value, label: PERSONA_LABELS[value] }));

const VERBOSITY_LABELS: Record<AiChatSettingsVerbosity, string> = {
  default: 'Default',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

const VERBOSITY_OPTIONS: { value: AiChatSettingsVerbosity; label: string }[] =
  AI_CHAT_SETTINGS_VERBOSITY.map((value) => ({ value, label: VERBOSITY_LABELS[value] }));

const REASONING_EFFORT_LABELS: Record<AiChatSettingsReasoningEffort, string> = {
  default: 'Default',
  off: 'Off',
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

const REASONING_EFFORT_OPTIONS: { value: AiChatSettingsReasoningEffort; label: string }[] =
  AI_CHAT_SETTINGS_REASONING_EFFORT.map((value) => ({
    value,
    label: REASONING_EFFORT_LABELS[value],
  }));

const WEB_SEARCH_LABELS: Record<AiChatSettingsWebSearch, string> = {
  default: 'Default',
  off: 'Off',
  on: 'On',
};

const WEB_SEARCH_OPTIONS: { value: AiChatSettingsWebSearch; label: string }[] =
  AI_CHAT_SETTINGS_WEB_SEARCH.map((value) => ({ value, label: WEB_SEARCH_LABELS[value] }));

const REASONING_SUMMARY_LABELS: Record<AiChatSettingsReasoningSummary, string> = {
  default: 'Default',
  off: 'Off',
  auto: 'Auto',
  concise: 'Concise',
  detailed: 'Detailed',
};

const REASONING_SUMMARY_OPTIONS: { value: AiChatSettingsReasoningSummary; label: string }[] =
  AI_CHAT_SETTINGS_REASONING_SUMMARY.map((value) => ({
    value,
    label: REASONING_SUMMARY_LABELS[value],
  }));

const ChatSettingsDialog: React.FC<ChatSettingsDialogProps> = ({
  open,
  onClose,
  settings,
  onSave,
  onReset,
}) => {
  const [local, setLocal] = React.useState<AiChatSettings>(settings);

  React.useEffect(() => {
    if (open) setLocal(settings);
  }, [open, settings]);

  const handleChange = <K extends keyof AiChatSettings>(field: K, value: AiChatSettings[K]) => {
    setLocal((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave(local);
    onClose();
  };

  const handleReset = () => {
    onReset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Chat Settings</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Model</InputLabel>
            <Select
              label="Model"
              value={local.model ?? 'default'}
              onChange={(e) => handleChange('model', e.target.value as AiChatSettingsModel)}
            >
              {MODEL_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Persona</InputLabel>
            <Select
              label="Persona"
              value={local.persona ?? 'default'}
              onChange={(e) => handleChange('persona', e.target.value as AiChatSettingsPersona)}
            >
              {PERSONA_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Verbosity</InputLabel>
            <Select
              label="Verbosity"
              value={local.verbosity ?? 'default'}
              onChange={(e) => handleChange('verbosity', e.target.value as AiChatSettingsVerbosity)}
            >
              {VERBOSITY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Reasoning Effort</InputLabel>
            <Select
              label="Reasoning Effort"
              value={local.reasoningEffort ?? 'default'}
              onChange={(e) =>
                handleChange('reasoningEffort', e.target.value as AiChatSettingsReasoningEffort)
              }
            >
              {REASONING_EFFORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Web Search</InputLabel>
            <Select
              label="Web Search"
              value={local.webSearch ?? 'default'}
              onChange={(e) => handleChange('webSearch', e.target.value as AiChatSettingsWebSearch)}
            >
              {WEB_SEARCH_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" fullWidth>
            <InputLabel>Show Reasoning</InputLabel>
            <Select
              label="Show Reasoning"
              value={local.reasoningSummary ?? 'default'}
              onChange={(e) =>
                handleChange('reasoningSummary', e.target.value as AiChatSettingsReasoningSummary)
              }
            >
              {REASONING_SUMMARY_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary">
            Changes apply on the next sent message.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} color="inherit" size="small">
          Reset to Defaults
        </Button>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" size="small">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatSettingsDialog;
